/**
 * LargeTextToggleCard - Accessibility toggle for large text mode
 * Shared between guest and registered profile views
 */

import { useTranslation } from 'react-i18next';
import { ZoomIn } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface LargeTextToggleCardProps {
  className: string;
}

export default function LargeTextToggleCard({ className }: LargeTextToggleCardProps) {
  const { t } = useTranslation();
  const { largeText, toggleLargeText } = useTheme();

  return (
    <div className={className}>
      <div className="flex items-center justify-between p-4 border border-hairline rounded-lg">
        <div className="flex items-center gap-3 flex-1">
          <ZoomIn className="w-5 h-5 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
          <div>
            <p className="font-medium text-ink">{t('profile.largeText.label')}</p>
            <p className="text-sm text-ink-muted">{t('profile.largeText.description')}</p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={largeText}
          aria-label={t('profile.largeText.label')}
          onClick={toggleLargeText}
          className="relative inline-flex min-h-[48px] min-w-[48px] items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 focus:ring-offset-canvas"
        >
          <span
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              largeText ? 'bg-accent' : 'bg-surface-3'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full transition-transform ${
                largeText ? 'translate-x-6 bg-on-accent' : 'translate-x-1 bg-ink-subtle'
              }`}
            />
          </span>
        </button>
      </div>
    </div>
  );
}
